import { useState, useEffect } from 'react'
import type * as React from 'react'
import { useBrainfishWidget } from '../../hooks/useBrainfishWidget'
import { closeBeacon, isBeaconOpen } from '../../hooks/useHelpScoutBeacon'
import {
  CircleHelpIcon,
  ChevronDownIcon,
  IconButton,
} from '@buffer-mono/popcorn'
import type { Account } from '../../types'
import { getActiveProductFromPath } from '../../utils/getProduct'
import styles from './HelpWidget.module.css'

interface SplitFlag {
  isReady: boolean
  isEnabled: boolean
}

interface HelpWidgetProps {
  beaconInHelpMenuFlag: SplitFlag
  user: Account
  isFixed?: boolean
  className?: string
  isButtonHidden?: boolean
  onButtonVisibilityChange?: (hidden: boolean) => void
}

export const HelpWidget: React.FC<HelpWidgetProps> = (props) => {
  const {
    beaconInHelpMenuFlag,
    isFixed = true,
    className,
    isButtonHidden = false,
    onButtonVisibilityChange,
  } = props

  // Brainfish is the permanent help provider; the Beacon is only ever opened
  // via Brainfish's escalation to email support.
  const shouldMoveToHelpMenu =
    beaconInHelpMenuFlag.isReady && beaconInHelpMenuFlag.isEnabled

  const {
    openBrainfish,
    closeBrainfish,
    isReady: isBrainfishReady,
    isOpen: isBrainfishOpen,
  } = useBrainfishWidget({
    user: props.user,
    onVisibilityChange: onButtonVisibilityChange,
  })

  // Track Beacon open state (Beacon can be opened by Brainfish escalation to email support)
  const [beaconOpenState, setBeaconOpenState] = useState(() => isBeaconOpen())

  useEffect(() => {
    const handleBeaconOpen = (): void => setBeaconOpenState(true)
    const handleBeaconClose = (): void => setBeaconOpenState(false)

    window.Beacon?.('on', 'open', handleBeaconOpen)
    window.Beacon?.('on', 'close', handleBeaconClose)

    return () => {
      window.Beacon?.('off', 'open', handleBeaconOpen)
      window.Beacon?.('off', 'close', handleBeaconClose)
    }
  }, [])

  // Widget is "open" if either Brainfish or Beacon is open
  const isOpen = isBrainfishOpen || beaconOpenState

  const handleToggle = (): void => {
    // If Beacon is open (e.g. from Brainfish escalation to email), close it
    if (beaconOpenState) {
      closeBeacon()
      return
    }

    isBrainfishOpen ? closeBrainfish() : openBrainfish()
  }

  // Show nothing until Brainfish is ready
  if (!isBrainfishReady) {
    return null
  }

  // Hide the standalone trigger when the help entry point lives in the help menu
  if (shouldMoveToHelpMenu) {
    return null
  }

  if (!isFixed) {
    if (isButtonHidden || beaconOpenState) {
      return null
    }
    return (
      <IconButton
        label="Help center"
        variant="secondary"
        onClick={handleToggle}
        className={className}
      >
        {isOpen ? (
          <ChevronDownIcon size="large" stroke={1} />
        ) : (
          <CircleHelpIcon size="large" stroke={1} />
        )}
      </IconButton>
    )
  }

  const activeProduct = getActiveProductFromPath()
  const getProductClass = (): string => {
    switch (activeProduct) {
      case 'engage':
        return styles.engage
      case 'start_page':
        return styles.startPage
      default:
        return styles.default
    }
  }

  return (
    <button
      className={`${styles.triggerButton} ${getProductClass()}${
        isButtonHidden || beaconOpenState ? ` ${styles.hidden}` : ''
      }`}
      onClick={handleToggle}
      aria-label="Help center"
    >
      {isOpen ? (
        <ChevronDownIcon size="medium" stroke={1} />
      ) : (
        <CircleHelpIcon size="medium" stroke={1} />
      )}
    </button>
  )
}
